import React, { useEffect, useState } from 'react';

import { IconSparkles } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { DroxyModal } from 'components/Droxy/DroxyModal';
import { CreateMemoryGenerateView } from 'pages/ManageSmartAssistant/Sections/ConversationSections/CreateMemoryButton/CreateMemoryModal/CreateMemoryGenerateView';
import { CreateMemoryPreviewView } from 'pages/ManageSmartAssistant/Sections/ConversationSections/CreateMemoryButton/CreateMemoryModal/CreateMemoryPreviewView';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { MAX_MEMORIES_PER_SMART_ASSISTANT } from 'shared/constants/memory';
import { SMART_ASSISTANT_QUERY_KEY } from 'shared/constants/query-keys';
import { Conversation } from 'shared/interfaces/Conversation';
import { MemoryType } from 'shared/interfaces/Memory';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';

type CreateMemoryModalView = 'generate' | 'preview';

interface CreateMemoryModalProps {
  opened: boolean;
  onClose: () => void;
  smartAssistant: SmartAssistant;
  // In Memories view, we don't have a conversation associated with the memory
  conversation?: Conversation;
  memoryBeingEditedIndex?: number;
}

export function CreateMemoryModal({
  opened,
  onClose,
  smartAssistant,
  conversation,
  memoryBeingEditedIndex
}: CreateMemoryModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [modalView, setModalView] = useState<CreateMemoryModalView>('generate');
  const [isSavingMemory, setIsSavingMemory] = useState(false);
  const [generatedMemory, setGeneratedMemory] = useState<string>('');

  // If we're editing a memory, we need to set the modal view to preview and the generated memory to the memory being edited
  useEffect(() => {
    if (memoryBeingEditedIndex !== undefined && smartAssistant.memories) {
      setModalView('preview');
      setGeneratedMemory(smartAssistant.memories[memoryBeingEditedIndex].genericMemoryContent.content);
    }
  }, [memoryBeingEditedIndex]);

  const saveMutation = useMutation({
    mutationFn: SmartAssistantService.updateSmartAssistant,
    onSuccess: (updatedSmartAssistant: SmartAssistant) => {
      queryClient.setQueryData([SMART_ASSISTANT_QUERY_KEY, { smartAssistantId: smartAssistant._id }], () => {
        return updatedSmartAssistant;
      });

      if (memoryBeingEditedIndex !== undefined) {
        NotificationService.showResult(true, 'FEED_BACK.MEMORY.SUCCESS.UPDATE');
      } else {
        NotificationService.showResult(true, 'FEED_BACK.MEMORY.SUCCESS.CREATE');
      }
    },
    onError: (axiosError: AxiosError) => {
      if (axiosError.response?.status === 400) {
        NotificationService.showResult(false, 'FEED_BACK.MEMORY.FAILURE.INVALID_MEMORY');
        return;
      }

      if (memoryBeingEditedIndex !== undefined) {
        NotificationService.showResult(false, 'FEED_BACK.MEMORY.FAILURE.UPDATE');
      } else {
        NotificationService.showResult(false, 'FEED_BACK.MEMORY.FAILURE.CREATE');
      }
    }
  });

  const handleMemoryGenerated = (memory: string) => {
    setGeneratedMemory(memory);
    setModalView('preview');
  };

  const cleanUpModal = () => {
    setModalView('generate');
    setGeneratedMemory('');
  };

  const handleSaveMemory = async () => {
    if (!generatedMemory) return;

    setIsSavingMemory(true);

    // If we're editing a memory, we need to exclude it from the existing memories
    const existingMemories =
      memoryBeingEditedIndex !== undefined && smartAssistant.memories
        ? smartAssistant.memories.filter((_, index) => index !== memoryBeingEditedIndex)
        : smartAssistant.memories || [];

    await saveMutation.mutateAsync({
      id: smartAssistant._id,
      // We should always add the new memory at the beginning of the memories list
      memories: [
        {
          type: MemoryType.GENERIC,
          genericMemoryContent: {
            content: generatedMemory
          }
        },
        ...existingMemories
      ]
    });
    setIsSavingMemory(false);
    cleanUpModal();
    onClose();
  };

  const handleModalClose = () => {
    onClose();
  };

  return (
    <DroxyModal
      opened={opened}
      titleIcon={<IconSparkles />}
      onClose={handleModalClose}
      size="xl"
      title={t('SMART_ASSISTANT.MEMORIES_SECTION.MODAL.TITLE')}
      description={t('SMART_ASSISTANT.MEMORIES_SECTION.MODAL.DESCRIPTION', {
        maxMemories: MAX_MEMORIES_PER_SMART_ASSISTANT
      })}>
      {modalView === 'generate' ? (
        <CreateMemoryGenerateView
          conversation={conversation}
          smartAssistant={smartAssistant}
          onMemoryGenerated={handleMemoryGenerated}
          onCancel={onClose}
        />
      ) : (
        <CreateMemoryPreviewView
          initialGeneratedMemory={generatedMemory}
          onGeneratedMemoryChange={setGeneratedMemory}
          isSaving={isSavingMemory}
          onSave={handleSaveMemory}
          onBack={cleanUpModal}
          smartAssistant={smartAssistant}
          memoryBeingEditedIndex={memoryBeingEditedIndex}
          showBackButton={memoryBeingEditedIndex === undefined}
        />
      )}
    </DroxyModal>
  );
}
